/**
 * dayViewAvailability.js
 * Pure transforms for the day-view (fullDayAvailability) booking experiment: classify each
 * availability slot, bucket slots into Morning/Afternoon/Night, drop today's past / under-lead-time
 * slots, build the GraphQL variables, and adapt a chosen slot into the existing selectedAppointment
 * shape. No React, no network — unit-tested in isolation.
 */
import { DateTime } from 'luxon'
import { pick } from 'lodash'

export const SLOT_VARIANT = {
  AVAILABLE: 'available',
  ALTERNATE: 'alternate',
  UNAVAILABLE: 'unavailable',
}

export const TIME_OF_DAY = {
  MORNING: 'morning',
  AFTERNOON: 'afternoon',
  NIGHT: 'night',
}

// Single source of truth for the slot cadence: the step we request from the server
// (timeSlotStepMinutes) and the step the grid iterates in must stay identical, or grid
// positions and returned slots stop lining up.
export const TIME_SLOT_STEP_MINUTES = 30

/**
 * classifySlot: derive the render variant for one fullDayAvailability slot. We only request
 * `appointment` and `partialAppointment`, so a slot with neither (including requested-only, which we
 * do not request) is unavailable. A bookable variant additionally requires at least one service:
 * dayViewSlotToSelectedAppointment feeds getAppointmentEndTime and the Step Five summary, both of which
 * crash on an empty appointmentServices, so a serviceless (partial or full) appointment is treated as
 * unavailable rather than offered as a slot that would break on tap. The backend can send
 * `appointmentServices: null` (not just omit it), so coerce before measuring length.
 * @param {object} slot
 * @returns {string} SLOT_VARIANT value
 */
export const classifySlot = slot => {
  if (slot?.appointment && (slot.appointment.appointmentServices || []).length) {
    return SLOT_VARIANT.AVAILABLE
  }
  if (slot?.partialAppointment && (slot.partialAppointment.appointmentServices || []).length) {
    return SLOT_VARIANT.ALTERNATE
  }
  return SLOT_VARIANT.UNAVAILABLE
}

/**
 * describeAlternate: derive a short human label for a partial (alternate) slot by diffing the
 * services it can actually cover against what the shopper requested. Mirrors mantis derivePartialLabel:
 * a dropped service reads "{name} only" (one left) or "without {names}" (several), and a slot that
 * keeps every service but drops an add-on reads "Skip add-on(s)".
 * @param {Array} partialServices partialAppointment.appointmentServices
 * @param {Array} serviceRequests booking-state serviceRequests ({ serviceId, addOnServiceIds })
 * @param {Array} marketServices market services (for id -> name), from useAllMarketServices
 * @returns {string} label
 */
export const describeAlternate = (partialServices = [], serviceRequests = [], marketServices = []) => {
  // Coerce here rather than lean on the default param: the backend can send `appointmentServices: null`
  // (not just omit it), and the default only fires for `undefined` — a null would crash the `.map` below.
  const services = partialServices || []
  const nameFor = id => (marketServices || []).find(ms => ms.service?.id === id)?.service?.name || 'this service'
  const matchedBaseIds = new Set(services.map(svc => svc.baseServiceId))

  const missing = serviceRequests.filter(sr => !matchedBaseIds.has(sr.serviceId))

  const droppedAddOn = serviceRequests.some(sr => {
    if (!matchedBaseIds.has(sr.serviceId) || !(sr.addOnServiceIds || []).length) {
      return false
    }
    const matched = services.find(svc => svc.baseServiceId === sr.serviceId)
    const kept = new Set(matched?.addOnServiceIds || [])
    return sr.addOnServiceIds.some(id => !kept.has(id))
  })

  if (missing.length) {
    const keptNames = serviceRequests.filter(sr => matchedBaseIds.has(sr.serviceId)).map(sr => nameFor(sr.serviceId))
    if (keptNames.length === 1) {
      return `${keptNames[0]} only`
    }
    return `without ${missing.map(sr => nameFor(sr.serviceId)).join(' + ')}`
  }

  if (droppedAddOn) {
    return 'Skip add-ons'
  }

  return 'Partial match'
}

/**
 * timeOfDayFor: bucket an hour-of-day into morning/afternoon/night.
 * Morning < 12:00, Afternoon 12:00–<19:00, Night >= 19:00.
 * @param {number} hour
 * @returns {string} TIME_OF_DAY value
 */
export const timeOfDayFor = hour => {
  if (hour < 12) {
    return TIME_OF_DAY.MORNING
  }
  if (hour < 19) {
    return TIME_OF_DAY.AFTERNOON
  }
  return TIME_OF_DAY.NIGHT
}

/**
 * buildDayView: build the day's slot grid from the market's booking window
 * (firstAppointmentTime..lastAppointmentTime, minutes since midnight, 30-min steps) and map the raw
 * fullDayAvailability slots onto it. Every grid position renders — a position with no bookable result,
 * or one earlier than now + the market lead-time floor (today only), is UNAVAILABLE — so the number of
 * slots is consistent across days instead of starting late. Alternate (partial) slots carry an
 * `alternateLabel` describing the reduced service set.
 * @param {Array} slots raw slots ({ startDateTime, appointment, partialAppointment })
 * @param {object} opts { timezone, now (DateTime), dayStart (DateTime, selected day 00:00),
 *   minimumAdvanceBookingMinutes, firstAppointmentTime, lastAppointmentTime, serviceRequests, marketServices }
 * @returns {{ morning:Array, afternoon:Array, night:Array, hasAvailable:boolean, hasAlternate:boolean, hasBookable:boolean }}
 */
export const buildDayView = (
  slots = [],
  {
    timezone,
    now,
    dayStart,
    minimumAdvanceBookingMinutes = 0,
    firstAppointmentTime,
    lastAppointmentTime,
    timeSlotStepMinutes = TIME_SLOT_STEP_MINUTES,
    serviceRequests = [],
    marketServices = [],
  } = {},
) => {
  // index returned slots by minute-of-day (in market tz) so grid positions can look them up
  const byMinute = new Map()
  slots.forEach(slot => {
    const dt = DateTime.fromISO(slot.startDateTime, { zone: timezone })
    byMinute.set(dt.hour * 60 + dt.minute, slot)
  })

  // Grid bounds: the market window when provided, else the envelope of what was returned.
  const returnedMinutes = [...byMinute.keys()]
  const startMinute = Number.isFinite(firstAppointmentTime)
    ? firstAppointmentTime
    : Math.min(...returnedMinutes, 0)
  const endMinute = Number.isFinite(lastAppointmentTime)
    ? lastAppointmentTime
    : Math.max(...returnedMinutes, 0)

  const base = dayStart || (now ? now.startOf('day') : null)
  const earliest = now ? now.plus({ minutes: minimumAdvanceBookingMinutes }) : null
  const buckets = { morning: [], afternoon: [], night: [] }
  let hasAvailable = false
  let hasAlternate = false

  for (let minute = startMinute; minute <= endMinute; minute += timeSlotStepMinutes) {
    const startDateTime = base ? base.set({ hour: Math.floor(minute / 60), minute: minute % 60 }) : null
    const slot = byMinute.get(minute)

    let variant = classifySlot(slot)
    // today's past / under-lead-time positions still render, but are not bookable
    if (earliest && startDateTime && startDateTime < earliest) {
      variant = SLOT_VARIANT.UNAVAILABLE
    }

    if (variant === SLOT_VARIANT.AVAILABLE) {
      hasAvailable = true
    } else if (variant === SLOT_VARIANT.ALTERNATE) {
      hasAlternate = true
    }

    buckets[timeOfDayFor(Math.floor(minute / 60))].push({
      startDateTime,
      label: startDateTime ? startDateTime.toFormat('h:mm a') : '',
      variant,
      appointment: (slot && slot.appointment) || null,
      partialAppointment: (slot && slot.partialAppointment) || null,
      alternateLabel:
        variant === SLOT_VARIANT.ALTERNATE
          ? describeAlternate(slot.partialAppointment.appointmentServices, serviceRequests, marketServices)
          : '',
    })
  }

  return { ...buckets, hasAvailable, hasAlternate, hasBookable: hasAvailable || hasAlternate }
}

/**
 * buildFullDayAvailabilityVariables: shape booking state into fullDayAvailability GraphQL variables.
 * Pick-a-pro and requested-time are out of scope, so maxPreferredProviders is 0 and providerIds are
 * omitted.
 * NOTE: AddressInput fields (street/city/state/zip/type) mirror the classic availability search — `type`
 * (Home/Office/Hotel, collected by the location-details modal) is forwarded so the full-day resolver can
 * apply location-specific availability/travel rules.
 * @param {object} args { date, address, serviceRequests, timeSlotStepMinutes }
 * @returns {object} GraphQL variables
 */
export const buildFullDayAvailabilityVariables = ({
  date,
  address,
  serviceRequests = [],
  timeSlotStepMinutes = TIME_SLOT_STEP_MINUTES,
}) => ({
  date,
  timeSlotStepMinutes,
  address: pick(address, ['street', 'city', 'state', 'zip', 'type']),
  services: serviceRequests.flatMap(sr => {
    const instances = sr.instances || 1
    const service = {
      baseServiceId: sr.serviceId,
      addOnServiceIds: sr.addOnServiceIds || [],
      instances: sr.backToBack ? instances : 1
    }
    // GraphQL's instances means consecutive services with one pro. Separate
    // requests preserve the cart's simultaneous, one-pro-per-person choice.
    return sr.backToBack
      ? [service]
      : Array.from({ length: instances }, () => ({ ...service }))
  }),
  maxPreferredProviders: 0,
})

/**
 * dayViewSlotToSelectedAppointment: adapt a fullDayAvailability slot appointment into the
 * selectedAppointment shape the rest of the booking flow + REST booking POST consume. The encrypted
 * bookingTokens are authoritative server-side; per-service ids/times are carried for client-side
 * cart/price/UI reads only (mirrors mantis toAppointmentModel).
 * @param {object} slotAppointment slot.appointment ({ totalPrice, bookingTokens, appointmentServices })
 * @param {DateTime} startDateTime the slot's start (Luxon DateTime in market timezone)
 * @param {object} ctx { marketId, marketShortName, timezone, addressId }
 * @returns {object} selectedAppointment
 */
export const dayViewSlotToSelectedAppointment = (
  slotAppointment,
  startDateTime,
  { marketId, marketShortName, timezone, addressId },
) => {
  const actualStartDateTime = Math.floor(startDateTime.toSeconds())
  const providerEndTimes = new Map()

  return {
    actualStartDateTime,
    startDateTime: startDateTime.toISO(),
    totalPrice: slotAppointment.totalPrice,
    finalPriceWithTaxAndTip: null, // null triggers Five.js price back-fill via calcApptPrice
    additionalFees: [], // never undefined — pages/book/index.js + Five.js call .length/.map
    bookingTokens: slotAppointment.bookingTokens, // authoritative for the REST booking POST
    addressId,
    address: { market: { id: marketId, shortName: marketShortName, timezone } },
    appointmentServices: (slotAppointment.appointmentServices || []).map(svc => {
      // Full-day results list a pro's consecutive instances in booking order,
      // but expose their signed start times only inside encrypted tokens. Rebuild
      // those offsets for checkout; different pros still begin simultaneously.
      const serviceStart = providerEndTimes.get(svc.providerId) || actualStartDateTime
      if (svc.providerId) {
        providerEndTimes.set(svc.providerId, serviceStart + svc.duration * 60)
      }
      return {
        bookingToken: svc.bookingToken,
        baseServiceId: svc.baseServiceId,
        serviceId: svc.baseServiceId, // alias; server restores the real ids from the token
        addOnServiceIds: svc.addOnServiceIds || [],
        providerId: svc.providerId,
        providerName: svc.providerName,
        duration: svc.duration,
        price: svc.price,
        startDateTime: serviceStart
      }
    }),
  }
}
